import CategoryIcon from '@mui/icons-material/Category';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import LabelIcon from '@mui/icons-material/Label';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  Tooltip,
  Typography
} from '@mui/material';
import ConfirmationButtonDialog from 'components/ui/feedback/ConfirmationButtonDelete';
import { useTranslation } from 'react-i18next';
import { IDocument } from '../types';

interface DocumentCardProps {
  document: IDocument;
  canDelete: boolean;
  onDownload: (id: number) => void;
  onDelete: (id: number) => void;
}

export const DocumentCard = ({ document, canDelete, onDownload, onDelete }: DocumentCardProps) => {
  const { t } = useTranslation();

  const getIcon = (filename: string) => {
    const _filename = (filename || '').toLowerCase();
    if (_filename.endsWith('.pdf')) return <PictureAsPdfIcon fontSize="large" color="error" />;
    if (['_doc', '.docx', '.otd', '.pages'].some((ext) => _filename.endsWith(ext))) {
      return <TextSnippetIcon fontSize="large" color="info" />;
    }
    if (['_png', '.jpg', '.jpeg'].some((ext) => _filename.endsWith(ext))) {
      return <ImageIcon fontSize="large" color="success" />;
    }
    return <TextSnippetIcon fontSize="large" color="action" />;
  };

  // const getIcon = (filename) => {
  //   const _filename = (filename ?? '').toLowerCase();

  //   if (_filename.endsWith('.pdf')) {
  //     return <PictureAsPdfIcon fontSize="large" color="error" />;
  //   } else if (
  //     _filename.endsWith('.doc') ||
  //     _filename.endsWith('.docx') ||
  //     _filename.endsWith('.otd') ||
  //     _filename.endsWith('.pages')
  //   ) {
  //     return <TextSnippetIcon fontSize="large" color="info" />;
  //   } else if (_filename.endsWith('.png') || _filename.endsWith('.jpg')) {
  //     return <ImageIcon fontSize="large" color="info" />;
  //   } else {
  //     return <TextSnippetIcon fontSize="large" />;
  //   }
  // };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          borderColor: 'divider',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.08)',
            borderColor: 'primary.light',
            bgcolor: 'action.hover'
          }
        }}>
        <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Tooltip title={document.description || document.name} placement="top" enterDelay={400}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'start', minWidth: 0 }}>
              <Box sx={{ display: 'flex', flexShrink: 0, mt: 0.5 }}>
                {getIcon(document.attachment)}
              </Box>
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    mb: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    wordBreak: 'break-word'
                  }}>
                  {document.name}
                </Typography>
                {document.category && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ color: 'text.secondary', mb: 0.5 }}>
                    <CategoryIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {document.category}
                    </Typography>
                  </Box>
                )}
                {document.tipology && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{ color: 'text.secondary' }}>
                    <LabelIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                      {document.tipology}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Tooltip>
        </CardContent>
        <Divider sx={{ mx: 2 }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            bgcolor: 'background.paper',
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8
          }}>
          <Tooltip title={t('actions.download') || 'Scarica'}>
            <IconButton
              size="small"
              color="info"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(document.id);
              }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canDelete && (
            <Box onClick={(e) => e.stopPropagation()} sx={{ display: 'inline-flex' }}>
              <ConfirmationButtonDialog id={document.id} handleDelete={onDelete} iconButton />
            </Box>
          )}
        </Box>
      </Card>
    </Grid>
  );
};
